import { useState } from "react";
import { useApp } from "../../contexts/AppContext";

const REPORT_REASONS = [
  "虚偽・誇大な内容が含まれている",
  "不適切・有害なコンテンツ",
  "著作権・知的財産権の侵害",
  "スパム・宣伝目的の不正投稿",
  "詐欺・フィッシングの疑い",
  "差別・ハラスメントに該当する内容",
  "その他",
];

export default function ReportModal() {
  const { isReportModalOpen, reportTarget, closeReport } = useApp();
  const [selectedReason, setSelectedReason] = useState("");
  const [detail, setDetail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  if (!isReportModalOpen && !submitted) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedReason) return;
    setSubmitting(true);
    await new Promise((r) => setTimeout(r, 900));
    setSubmitting(false);
    setSubmitted(true);
  };

  const handleClose = () => {
    closeReport();
    setTimeout(() => {
      setSelectedReason("");
      setDetail("");
      setSubmitted(false);
    }, 300);
  };

  const typeLabel: Record<string, string> = {
    video: "動画講座",
    kit: "教材キット",
    provider: "事業者ページ",
    review: "レビュー",
    message: "メッセージ",
  };

  return (
    <div
      className={`fixed inset-0 z-[100] flex items-center justify-center p-4 transition-all duration-300 ${
        isReportModalOpen ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
    >
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />
      <div
        className={`relative bg-white rounded-2xl w-full max-w-lg overflow-hidden transition-all duration-300 ${
          isReportModalOpen ? "scale-100 translate-y-0" : "scale-95 translate-y-4"
        }`}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-stone-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 flex items-center justify-center rounded-full bg-red-50">
              <i className="ri-flag-line text-red-400 text-sm" />
            </div>
            <div>
              <h3 className="font-serif text-charcoal text-base">通報する</h3>
              {reportTarget && (
                <p className="text-xs text-charcoal/40 font-sans mt-0.5">
                  {typeLabel[reportTarget.type]}：{reportTarget.title}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 flex items-center justify-center text-charcoal/30 hover:text-charcoal cursor-pointer transition-colors"
          >
            <i className="ri-close-line text-xl" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-6">
          {submitted ? (
            <div className="text-center py-6">
              <div className="w-14 h-14 flex items-center justify-center rounded-full bg-teal/10 mx-auto mb-4">
                <i className="ri-checkbox-circle-fill text-3xl text-teal" />
              </div>
              <h4 className="font-serif text-deep-green text-lg mb-2">通報を受け付けました</h4>
              <p className="text-sm text-charcoal/60 font-sans leading-relaxed">
                内容を確認の上、必要に応じて対応いたします。<br />
                通報内容は管理者のみが確認できます。
              </p>
              <button
                onClick={handleClose}
                className="mt-6 px-8 py-3 bg-deep-green text-white rounded-full text-sm font-sans cursor-pointer hover:bg-teal transition-colors whitespace-nowrap"
              >
                閉じる
              </button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <p className="text-sm font-sans text-charcoal mb-3">通報の理由を選択してください</p>
                <div className="space-y-2">
                  {REPORT_REASONS.map((reason) => (
                    <label
                      key={reason}
                      className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all duration-200 ${
                        selectedReason === reason
                          ? "border-red-300 bg-red-50"
                          : "border-stone-100 hover:border-stone-200 bg-stone-50/50"
                      }`}
                    >
                      <input
                        type="radio"
                        name="reason"
                        value={reason}
                        checked={selectedReason === reason}
                        onChange={() => setSelectedReason(reason)}
                        className="cursor-pointer accent-red-400"
                      />
                      <span className="text-sm font-sans text-charcoal/80">{reason}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-sans text-charcoal/50 mb-1.5 block">
                  詳細・補足（任意）
                </label>
                <textarea
                  value={detail}
                  onChange={(e) => setDetail(e.target.value)}
                  maxLength={500}
                  rows={3}
                  placeholder="具体的な状況や補足をご記入ください（500文字以内）"
                  className="w-full border border-stone-200 rounded-xl px-4 py-3 text-sm font-sans text-charcoal placeholder-charcoal/30 focus:outline-none focus:border-red-300 transition-colors resize-none"
                />
                <p className="text-xs text-charcoal/30 font-sans text-right mt-1">{detail.length}/500</p>
              </div>

              <div className="bg-stone-50 rounded-xl p-3">
                <p className="text-xs text-charcoal/50 font-sans leading-relaxed">
                  <i className="ri-information-line mr-1 text-charcoal/40" />
                  通報は匿名で行われます。虚偽の通報はアカウント停止の対象となる場合があります。
                  管理者はサービス品質維持のため必要に応じてメッセージ・コンテンツを確認できます。
                </p>
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  type="button"
                  onClick={handleClose}
                  className="flex-1 py-3 rounded-full border border-stone-200 text-sm font-sans text-charcoal/60 hover:border-stone-300 transition-colors cursor-pointer whitespace-nowrap"
                >
                  キャンセル
                </button>
                <button
                  type="submit"
                  disabled={!selectedReason || submitting}
                  className="flex-1 py-3 rounded-full bg-red-400 text-white text-sm font-sans cursor-pointer hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed transition-colors whitespace-nowrap"
                >
                  {submitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin" />送信中
                    </span>
                  ) : (
                    "通報を送信する"
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
